<script lang="ts" setup>
import { useI18n } from '@/plugins/i18n'
import DocItem from '../Common/DocItem.ce.vue';
import FilterDropdown from '@/components/Search/FilterDropdown.ce.vue';
import SelectedFiltersHeader from '../Common/SelectedFiltersHeader/index.ce.vue'
import SaiDropDown from '@/components/common/SaiDropDown/index.ce.vue';
import iconMain from '@/assets/img/icon-search-result-sort.svg';
import iconOpen from '@/assets/img/icon-search-result-sort-close.svg';
import iconClose from '@/assets/img/icon-search-result-sort-close.svg';
import { useSearchStore } from '@/components/Search/store/index.ts'
import { useSearchActions } from '@/components/Search/SearchResults/Composables/useSearchActions'
import SaiPagination from '@/components/common/SaiPagination/index.ce.vue';
import NoResult from '../Common/NoResult/index.ce.vue';
import { COLLECTIONS_SEARCH_TAB, COLLECTIONS_FILTER_KEYS } from '@/components/Search/constants.ts';
import InitialEmptyResultView from '@/components/Search/SearchResults/Common/InitialEmptyResultView/index.ce.vue';
import SaveSearch from '@/components/Search/SearchResults/Common/SaveSearch/index.ce.vue';
import ExportSearch from '@/components/Search/SearchResults/Common/ExportSearch/index.ce.vue';

//store
const searchStore = useSearchStore()

//translations
const i18nInstance: any = useI18n()
const { t } = i18nInstance

const exportFieldOptions: {key: string, val: string}[] = [
    {key: 'docId', val: t('docId')},
    {key: 'name', val: t('name')},
    {key: 'title', val: t('title')},
    {key: 'status', val: t('status')},
    {key: 'language', val: t('language')},
    {key: 'publisher', val: t('publisher')},
    {key: 'country', val: t('country')},
    {key: 'latestVersion', val: t('latestVersion')},
    {key: 'versionDate', val: t('versionDate')},
    {key: 'summary', val: t('summary')},
];

const { 
  isAllSelected,
  handleSelectItem,
  handleDocAction,
  handleSelectAllItem,
  handleBulkDocAction,
  removeFilter,
  clearAllFilters,
  saveFilters,
  getResultActionOptions,
  getResultsRecordTotal,
  getResultSummaryText,
  isResultLoaded,
  getDocOffset,
  handleExportFieldSelection,
  handleExportFormatSelection,
  handleExportSearchResults,
  handleExportClose,
  handleProductLinkClick
} = useSearchActions(COLLECTIONS_SEARCH_TAB, COLLECTIONS_FILTER_KEYS)

const gotToPage = (pageNum: number) => {
  searchStore.searchCollections(true, pageNum);
};

const arrCollectionSearchTips= [
  'noResultCollectionSearchTip1',
  'noResultCollectionSearchTip2',
  'noResultCollectionSearchTip3',
  'noResultCollectionSearchTip4',
  'noResultCollectionSearchTip5',
  'noResultCollectionSearchTip6'
];


</script>

<template>
    <InitialEmptyResultView  v-if="!isResultLoaded && !searchStore[`${COLLECTIONS_SEARCH_TAB}.result.loading`]" 
    :title="t('findStandardsInCollections')" :subTitle ="t('typeInSearchToGetStarted')"></InitialEmptyResultView>
    <div v-if="isResultLoaded && getResultsRecordTotal() === 0" class="search-result search-result--noresult">
        <template v-if="searchStore[`${COLLECTIONS_SEARCH_TAB}.resultData`]?.nohits">
          <NoResult :searchTerms="searchStore[`${COLLECTIONS_SEARCH_TAB}.resultData`]?.searchTerms" :searchTips="arrCollectionSearchTips"/>
        </template>
    </div>
    <div v-if="isResultLoaded && getResultsRecordTotal() > 0" class="search-result">
      <SelectedFiltersHeader :filterKeys="COLLECTIONS_FILTER_KEYS" @removeFilter="removeFilter" @clearAll="clearAllFilters" @saveFilters="saveFilters"/>
      <div class="search-result--summary-for-mobile">
        <div v-if="getResultSummaryText()" class="search-results-res">{{t('resultsFor', [getResultsRecordTotal().toString(), getResultSummaryText()])}}</div>
        <div v-else class="search-results-res">{{getResultsRecordTotal().toString()}} {{ " " + t('results') }}</div>
      </div>
      <div class="search-result--header">
        <div class="search-result--header-left">
          <template v-if="getResultActionOptions().length">
            <div class="doc-action--select-all">
              <img v-if="!isAllSelected" src="@/assets/img/icon-checkbox.svg" @click="handleSelectAllItem"/>
              <img v-if="isAllSelected" src="@/assets/img/icon-checkbox-active.svg" @click="handleSelectAllItem"/>
              {{ t('batchAction') }}
            </div>
            <div class="doc-action">
              <SaiDropDown 
                className="dropdown bulk-action"
                placeholder="Action"
                :multiple="false"
                name="collection-select-all"
                :hasClear="false"
                :options="getResultActionOptions()"
                :iconOpen="iconClose"
                :iconClose="iconClose"
                :closeOnSelect="true"
                :showSelectCountInHeader="false"
                @updateSelectedItems="handleBulkDocAction"
                :closeOnClickOutside="true"
            />
            </div>
          </template>
        </div>
        <div class="search-result--header-center">
          <div v-if="getResultSummaryText()" class="search-results-res">{{t('resultsFor', [getResultsRecordTotal().toString(), getResultSummaryText()])}}</div>
          <div v-else class="search-results-res">{{getResultsRecordTotal().toString()}} {{ " " + t('results') }}</div>
        </div>
        <div class="search-result--header-right">
            <FilterDropdown
              className="dropdown result-sort" 
              :placeholder="t('sort')" 
              :placeholderPrefix="`${t('sort')}:`"
              name="collections_sort" 
              :multiple="false"
              :field="`${COLLECTIONS_SEARCH_TAB}.result.sort`"
              :lookupField="`lookUpOptions.${COLLECTIONS_SEARCH_TAB}.sortOptions`"
              :translate="true"
              :hasClear="false" 
              :iconMain="iconMain"
              :iconOpenMain="iconMain"
              :iconOpen="iconOpen"
              :iconClose="iconClose"
              :searchable="false"
              :showMoreLink="false"
              :closeOnClickOutside="true"
              :showSelectCountInHeader="false"
              :closeOnSelect="true"
              :showSelectedInPlaceholder="true"
              @updateSelectedItems="() => searchStore.searchCollections(true)"
              />
            <SaveSearch :searchType="COLLECTIONS_SEARCH_TAB" :saveForMonthlyAlerts="false"></SaveSearch>
            <ExportSearch 
              :name="COLLECTIONS_SEARCH_TAB"
              :fieldOptions="exportFieldOptions"
              :status="searchStore[`${COLLECTIONS_SEARCH_TAB}.export.status`]"
              :selectedFormat="searchStore[`${COLLECTIONS_SEARCH_TAB}.export.format`]"
              :totalFound="searchStore[`${COLLECTIONS_SEARCH_TAB}.export.totalFound`] ?? 0"
              :selectedFields="searchStore[`${COLLECTIONS_SEARCH_TAB}.export.selectedFields`] ?? []"
              :searchContext="searchStore[`${COLLECTIONS_SEARCH_TAB}.resultData`]?.searchContext"
              :note="t('exportNoteStandards')"
              @handleExportFieldSelection="handleExportFieldSelection"
              @handleExportFormatSelection="handleExportFormatSelection"
              @handleExportSearchResults="handleExportSearchResults"
              @handleExportClose="handleExportClose">
            </ExportSearch>
        </div>
      </div>
      <div class="search-result-items">
        <DocItem v-for="(record, index) in searchStore['collections.resultData']?.records" 
          :item="record" 
          :docOffset="getDocOffset(index)"
          :searchContext="searchStore[`${COLLECTIONS_SEARCH_TAB}.resultData`]?.searchContext" 
          :activeCollectionName="searchStore[`${COLLECTIONS_SEARCH_TAB}.resultData`]?.activeCollectionName"
          @handleDocAction="handleDocAction"
          @handleSelectItem="handleSelectItem"
          @handleProductLinkClick="handleProductLinkClick(record, index)"
          :selectedItems="searchStore[`${COLLECTIONS_SEARCH_TAB}.resultData`]?.selectedItems ?? []"
          :key="index"/>
      </div>
      <div class="search-result--footer">
          <SaiPagination :current-page="searchStore[`${COLLECTIONS_SEARCH_TAB}.result.page`]"
            :total-items="searchStore[`${COLLECTIONS_SEARCH_TAB}.resultData`]?.hits ?? 0"
            :page-size="searchStore[`${COLLECTIONS_SEARCH_TAB}.resultData`]?.recordsPerPage ?? 0"
            @goToPage="gotToPage"></SaiPagination>
      </div>
    </div>
</template>
<style lang="scss">
    @use './style.scss';
</style>